/**
 * UsFinancialAid — Section 10 callout for US degrees, rendering the
 * institution-level financial-aid stats College Scorecard publishes:
 *
 *   • % of students receiving Pell Grants (federal need-based aid)
 *   • % taking federal student loans
 *   • Median student debt at graduation (completers)
 *   • Net price by family income bracket ($0-30k … $110k+)
 *
 * These numbers apply to every bachelor's programme at the uni — this
 * IS how US universities publish aid data federally. Programme-level
 * aid is set by the department and isn't federally reported.
 */
import { Card } from "@/components/ui/card";
import { PiggyBank } from "lucide-react";

export function UsFinancialAid({ degree, uni }) {
  if (degree?.source_provenance?.source !== "college_scorecard_api") return null;
  const aid = uni?.scorecard_aid;
  if (!aid) return null;
  const hasAny =
    aid.pell_grant_rate != null ||
    aid.median_debt_completers != null ||
    (aid.net_price_by_income && Object.keys(aid.net_price_by_income).length > 0);
  if (!hasAny) return null;

  const pct = (n) => `${Math.round(n * 100)}%`;
  const usd = (n) => (n < 0
    ? `-$${Math.abs(n).toLocaleString()}`
    : `$${n.toLocaleString()}`);

  const npBrackets = aid.net_price_by_income || {};
  const brackets = [
    ["0-30k",    "Family income under $30k"],
    ["30k-48k",  "$30k-$48k"],
    ["48k-75k",  "$48k-$75k"],
    ["75k-110k", "$75k-$110k"],
    ["110k+",    "$110k+"],
  ].filter(([k]) => npBrackets[k] != null);

  return (
    <Card
      className="p-4 border-teal-300 bg-teal-50/40"
      data-testid="us-financial-aid"
    >
      <div className="flex items-center gap-2 mb-3">
        <PiggyBank className="h-4 w-4 text-teal-800" />
        <div className="font-underground-sc text-[10px] tracking-wider text-teal-900">
          Federal aid profile · {uni.name}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-stone-800">
        {aid.pell_grant_rate != null && (
          <div>
            <b>Pell Grant recipients:</b> {pct(aid.pell_grant_rate)}
          </div>
        )}
        {aid.federal_loan_rate != null && (
          <div>
            <b>Federal loan borrowers:</b> {pct(aid.federal_loan_rate)}
          </div>
        )}
        {aid.median_debt_completers != null && (
          <div className="md:col-span-2">
            <b>Median debt at graduation:</b>{" "}
            {usd(aid.median_debt_completers)}
          </div>
        )}
      </div>

      {brackets.length > 0 && (
        <div className="mt-4">
          <div className="font-underground-sc text-[10px] tracking-wider text-teal-900 mb-2">
            Net price after aid — by family income
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
            {brackets.map(([k, label]) => (
              <div key={k} className="rounded border border-teal-200 bg-white p-2">
                <div className="text-[10px] text-stone-500 uppercase tracking-wider">{label}</div>
                <div className="font-editorial text-lg text-stone-900">
                  {usd(npBrackets[k])}<span className="text-xs text-stone-500">/yr</span>
                </div>
              </div>
            ))}
          </div>
          <div className="text-[11px] text-stone-500 italic mt-2">
            "Net price" = total annual cost of attendance minus the average
            grant + scholarship aid received. A negative value means the
            average student at that income level is paid more in aid than
            the sticker cost.
          </div>
        </div>
      )}

      <div className="mt-3 text-[11px] font-mono text-stone-500">
        Source: US Dept of Education Scorecard · federal IRS + IPEDS data ·
        fetched {aid.fetched_at || ""}
      </div>
    </Card>
  );
}

export default UsFinancialAid;
